/**
 * Haulage: the jobs a parked truck comes with, and what they pay.
 *
 * Get into a parked truck and it has a load waiting: a cargo of the kind that
 * truck carries, for somewhere a drive away along the roads. A box truck
 * does the local and regional runs, deliveries to a town or two away; an
 * articulated truck the long hauls, to the next city or the one after.
 *
 * Pay is by the kilometre on the road, as haulage is paid, plus a fixed sum
 * for the loading and unloading: so the longer the haul, the more it pays,
 * and the articulated truck, carrying three times the load, pays most of
 * all. Its diesel comes out of the same wallet (about 6 kr a kilometre laden),
 * which the rate leaves well clear of. A job has a time to be there by, from
 * the route's driving time and a margin, real minutes; late, it pays half.
 *
 * Pure: the destination's road and its name are found by the caller.
 */

import type { TruckKind } from '../../three/Vehicle.js';

/** What each truck carries. */
const CARGO: Record<TruckKind, readonly string[]> = {
  truck: [
    '14 pallets of groceries', 'furniture for a shop', '380 parcels', 'building supplies', 'kitchen appliances',
    'fresh flowers', 'office furniture', 'beverages', 'garden supplies', 'bathroom fittings',
  ],
  semi: [
    '33 pallets of frozen food', 'sawn timber', 'paper rolls', 'steel coils', 'car parts', 'retail goods',
    'machine parts', 'insulation boards', 'dairy products', 'flat-pack furniture', 'recycled cardboard',
  ],
};

/** How far a job's destination may be, straight from where the truck stands, km. */
const REACH: Record<TruckKind, { min: number; max: number }> = {
  truck: { min: 3, max: 35 },
  semi: { min: 25, max: 200 },
};

/** Kronor for the loading and unloading, and per kilometre on the road. */
const RATE: Record<TruckKind, { base: number; perKm: number }> = {
  truck: { base: 450, perKm: 25 },
  semi: { base: 900, perKm: 42 },
};

/** What a job's driver is given: the load, and where to try for its destination. */
export interface JobPlan {
  cargo: string;
  /** Straight-line target, km, and the bearing to look along, radians from north clockwise. */
  km: number;
  bearing: number;
}

/** A load and a direction and distance to find its destination by. */
export function planJob(kind: TruckKind, rng: () => number): JobPlan {
  const list = CARGO[kind];
  const reach = REACH[kind];
  // Shorter hauls more often than the longest: a uniform raised to a power above one leans short.
  const km = reach.min + (reach.max - reach.min) * rng() ** 1.6;
  return { cargo: list[Math.floor(rng() * list.length)], km, bearing: rng() * Math.PI * 2 };
}

/** A point `km` from (lat, lon) along `bearing`, radians from north clockwise. */
export function pointToward(lat: number, lon: number, km: number, bearing: number): { lat: number; lon: number } {
  const north = Math.cos(bearing) * km * 1000, east = Math.sin(bearing) * km * 1000;
  return {
    lat: lat + north / 111_320,
    lon: lon + east / (111_320 * Math.max(0.05, Math.cos((lat * Math.PI) / 180))),
  };
}

/** A named place: a city, town, village or suburb. */
export interface Place {
  lat: number;
  lon: number;
  name: string;
  kind: string;
}

/** Somewhere a truck unloads: an industrial estate, a warehouse, a retail or business park. */
export interface Site {
  lat: number;
  lon: number;
  name?: string;
  kind: string;
  /** m², where known. */
  area?: number;
}

/** Metres between two points, near enough at these distances. */
function metres(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const k = Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  return Math.hypot((a.lat - b.lat) * 111_320, (a.lon - b.lon) * 111_320 * k);
}

/** Towns before villages: freight goes where the trade is. */
const PLACE_PULL: Record<string, number> = { city: 0.55, town: 0.7, village: 1, suburb: 1 };
const SITE_WORD: Record<string, string> = {
  industrial: 'the industrial estate', warehouse: 'the warehouse', retail: 'the retail park', commercial: 'the business park',
};

/**
 * Where to deliver: the place nearest the job's aim, towns favoured, no nearer
 * the start than `minFrom` metres; and in it, if there is one, the site to
 * unload at, a big or named one favoured. Its name as the driver is told it.
 */
export function chooseDropOff(
  places: readonly Place[], sites: readonly Site[], target: { lat: number; lon: number },
  from: { lat: number; lon: number }, minFrom: number,
): { lat: number; lon: number; name: string } | undefined {
  let town: Place | undefined, best = Infinity;
  for (const p of places) {
    if (metres(p, from) < minFrom) continue;
    const score = metres(p, target) * (PLACE_PULL[p.kind] ?? 1);
    if (score < best) { best = score; town = p; }
  }
  if (town === undefined) return undefined;
  let site: Site | undefined;
  best = Infinity;
  for (const s of sites) {
    if (metres(s, from) < minFrom) continue;
    // The nearer the middle of town and the bigger the better; a named one better still.
    const score = metres(s, town) * (s.name !== undefined ? 0.3 : 1) / Math.sqrt(Math.max(1, (s.area ?? 0) / 30_000));
    if (score < best) { best = score; site = s; }
  }
  if (site === undefined) return { lat: town.lat, lon: town.lon, name: town.name };
  const name = site.name !== undefined && site.name !== town.name ? `${site.name}, ${town.name}`
    : `${SITE_WORD[site.kind] ?? 'the depot'} in ${town.name}`;
  return { lat: site.lat, lon: site.lon, name };
}

/** What a job pays, kronor, for its distance on the road, km: to the nearest ten. */
export function jobPay(kind: TruckKind, roadKm: number): number {
  const r = RATE[kind];
  return Math.round((r.base + r.perKm * roadKm) / 10) * 10;
}

/**
 * How long a job allows, real seconds, from the route's driving time as the
 * router reckons it: a truck goes slower than that (limited to 80 or 90,
 * and slow off the line), so half as long again, and ten minutes to find
 * the way, the fuel and the place.
 */
export function jobTime(routeSeconds: number): number {
  return routeSeconds * 1.5 + 600;
}

/** What is paid on delivery: in full, or half if late. */
export function payOnDelivery(pay: number, late: boolean): number {
  return late ? Math.round(pay / 2 / 10) * 10 : pay;
}

/** A job taken: what, from where, to where, for how much, by when. */
export interface Job {
  kind: TruckKind;
  cargo: string;
  /** Where to deliver it, on the road, and what the place is called. */
  to: { lat: number; lon: number };
  place: string;
  /** km on the road. */
  km: number;
  pay: number;
  /** When it is due, epoch ms. */
  due: number;
  /** The route there, for the maps. */
  line: Array<{ lat: number; lon: number }>;
}

/** How near the destination a truck must stand still to deliver, metres. */
export const DELIVER_RADIUS = 45;

/** Minutes and seconds, as "1:05:09" or "12:30", of a span in ms; "late" past due. */
export function timeLeft(ms: number): string {
  if (ms <= 0) return 'late';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
}
